import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useCarTechProfile, useCreateCarReport, useRentingReports } from '../hooks/useCarReports';
import { CreateReportDto, CreateReportDetail, ReportComment, carReportsApi } from '../api/carReportsApi';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useToast } from '@/components/ui/toast/Toast';
import Label from '@/components/form/Label';
import Input from '@/components/form/input/InputField';
import Button from '@/components/ui/button/Button';
import TextArea from '@/components/form/input/TextArea';
import InteractiveCarImage from '@/components/car/InteractiveCarImage';
import { fetchApi } from '@/shared/api/fetchApi';
import { Activity } from '@/features/activities/types/activityTypes';

interface CarReportFormProps {
  carId: string;
  rentingId?: string;
  activityId?: string;
  activities?: {
    delivery_to_client: string | null;
    pickup_from_client: string | null;
  };
  onSubmit: () => void;
  onClose: () => void;
  initialReportType?: 'delivery_to_client' | 'pickup_from_client' | 'handoff_to_service' | 'receiving_from_service';
  currentData?: Partial<CreateReportDto>;
  onDataChange?: (data: Partial<CreateReportDto>) => void;
  initialData?: Partial<CreateReportDto>;
}

interface DetailItem {
  id: string;
  detail: string;
  comments: ReportComment[];
  pendingImages: File[];
}

interface FormErrors {
  mileage?: string;
  fuel_level?: string;
  charge_level?: string;
  details?: string;
  detailFields?: { [key: string]: string | undefined };
}

const CarReportForm: React.FC<CarReportFormProps> = ({
  carId,
  rentingId,
  activityId,
  activities,
  onSubmit,
  onClose,
  initialReportType,
  currentData,
  onDataChange,
  initialData
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { data: techProfile, isLoading: isLoadingProfile } = useCarTechProfile(carId);
  const { data: reports, isLoading: isLoadingReports } = useRentingReports(rentingId || '');
  const createReport = useCreateCarReport();
  const { user } = useAuth();

  const [formData, setFormData] = useState<Partial<CreateReportDto>>(() => {
    // Инициализируем только один раз при монтировании
    return {
      renting: rentingId,
      activity: activityId,
      activity_type: initialReportType || initialData?.activity_type || 'delivery_to_client',
      mileage: initialData?.mileage ?? 0,
      fuel_level: initialData?.fuel_level ?? 0,
      charge_level: initialData?.charge_level ?? 0,
    };
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [details, setDetails] = useState<DetailItem[]>([]);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const isInitialized = useRef(false);

  // Инициализируем formData только один раз при монтировании
  useEffect(() => {
    if (!isInitialized.current) {
      // Первая инициализация - используем initialData только один раз
      if (initialData) {
        setFormData(prev => ({
          ...prev,
          ...initialData,
          renting: rentingId || initialData.renting,
          activity: activityId || initialData.activity,
          activity_type: initialReportType || initialData.activity_type || prev.activity_type || 'delivery_to_client',
        }));
      }
      isInitialized.current = true;
    }
    // После инициализации НЕ обновляем formData из initialData, чтобы не сбрасывать введенные пользователем данные
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Пустой массив зависимостей - выполняется только при монтировании

  // Обновляем только критичные поля при изменении пропсов (но не перезаписываем введенные данные)
  useEffect(() => {
    if (isInitialized.current) {
      setFormData(prev => ({
        ...prev,
        renting: rentingId || prev.renting,
        activity: activityId || prev.activity,
        activity_type: initialReportType || prev.activity_type || 'delivery_to_client',
      }));
    }
  }, [rentingId, activityId, initialReportType]);

  // Notify parent about data changes (только после инициализации, чтобы не создавать цикл)
  useEffect(() => {
    if (onDataChange && isInitialized.current) {
      onDataChange({
        ...formData,
        details: details.map(d => ({
          detail: d.detail,
          comments: d.comments
        }))
      });
    }
  }, [formData, details, onDataChange]);


  // Определяем, какие типы отчетов уже существуют
  const usedReportTypes = useMemo(() => {
    if (!reports) return new Set<string>();
    return new Set(reports.map(report => report.activity));
  }, [reports]);

  // Определяем доступные типы отчетов
  const availableReportTypes = useMemo(() => [
    { value: 'delivery_to_client', label: t('cars.report.options.upon_delivering'), disabled: usedReportTypes.has('delivery_to_client') },
    { value: 'pickup_from_client', label: t('cars.report.options.upon_picking_up'), disabled: usedReportTypes.has('pickup_from_client') },
    { value: 'handoff_to_service', label: t('cars.report.options.handoff_to_service'), disabled: usedReportTypes.has('handoff_to_service') },
    { value: 'receiving_from_service', label: t('cars.report.options.receiving_from_service'), disabled: usedReportTypes.has('receiving_from_service') }
  ], [usedReportTypes, t]);

  // Определяем начальный тип отчета
  const getInitialReportType = useMemo(() => {
    // Если указан initialReportType и он не отключен, используем его
    if (initialReportType && !usedReportTypes.has(initialReportType)) {
      return initialReportType;
    }
    // Иначе используем первый доступный тип
    return (availableReportTypes.find(type => !type.disabled)?.value as 'delivery_to_client' | 'pickup_from_client' | 'handoff_to_service' | 'receiving_from_service') || 'delivery_to_client';
  }, [initialReportType, availableReportTypes, usedReportTypes]);

  // Получаем список доступных деталей (исключая уже выбранные)
  const availableDetails = useMemo(() => {
    if (!techProfile) return [];
    const selectedDetails = new Set(details.map(d => d.detail));
    return techProfile.details.filter(d => !selectedDetails.has(d.detail));
  }, [techProfile, details]);

  // Обновляем тип активности при изменении activityId
  useEffect(() => {
    if (activityId) {
      // Получаем тип активности из API
      const fetchActivityType = async () => {
        try {
          const activity = await fetchApi<Activity>(`/api/v1/activities/${activityId}`);
          setFormData(prev => ({
            ...prev,
            activity: activityId,
            activity_type: activity.type
          }));
        } catch (error) {
          console.error('Failed to fetch activity type:', error);
        }
      };
      fetchActivityType();
    }
  }, [activityId]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.mileage || formData.mileage <= 0) {
      newErrors.mileage = t('cars.report.errors.mileage_positive');
    }

    if (formData.fuel_level !== undefined && formData.fuel_level > 100) {
      newErrors.fuel_level = t('cars.report.errors.fuel_level_max');
    }

    // Validate details
    const detailFieldErrors: { [key: string]: string } = {};
    let hasEmptyDetails = false;

    details.forEach(detail => {
      if (!detail.detail) {
        detailFieldErrors[detail.id] = t('cars.report.errors.detail_required');
        hasEmptyDetails = true;
      }
    });

    if (hasEmptyDetails) {
      newErrors.detailFields = detailFieldErrors;
      newErrors.details = t('cars.report.errors.select_all_details');
    }

    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;

    return isValid;
  };

  const handleAddDetail = (detailId: string) => {
    // Проверяем, не добавлена ли уже эта деталь
    if (details.some(d => d.detail === detailId)) {
      return;
    }

    const newId = crypto.randomUUID();
    setDetails(prev => [{
      id: newId,
      detail: detailId,
      comments: [{
        text: '',
        file_name: '',
        original_file_name: '',
        employee: user?.user_id || '',
        created_at: new Date().toISOString(),
      }],
      pendingImages: [],
    }, ...prev]);
  };

  const handleRemoveDetail = (detailId: string) => {
    setDetails(prev => prev.filter(d => d.detail !== detailId));
  };


  const handleDetailChange = (detailId: string, field: 'comment', value: string) => {
    setDetails(prev => prev.map(d => {
      if (d.detail !== detailId) return d;

      // Для комментария обновляем текст последнего текстового комментария
      // или создаем новый, если нет текстовых комментариев
      const textComment = d.comments.find(c => !c.file_url);
      if (textComment) {
        return {
          ...d,
          comments: d.comments.map(c =>
            c === textComment ? { ...c, text: value } : c
          )
        };
      } else {
        return {
          ...d,
          comments: [
            ...d.comments,
            {
              text: value,
              file_name: '',
              original_file_name: '',
              employee: user?.user_id || '',
              created_at: new Date().toISOString(),
            }
          ]
        };
      }
    }));
  };

  const handleAddPendingImage = (detailId: string, file: File) => {
    setDetails(prev => prev.map(d =>
      d.detail === detailId
        ? { ...d, pendingImages: [...d.pendingImages, file] }
        : d
    ));
  };

  const handleRemovePendingImage = (detailId: string, index: number) => {
    setDetails(prev => prev.map(d =>
      d.detail === detailId
        ? { ...d, pendingImages: d.pendingImages.filter((_, i) => i !== index) }
        : d
    ));
  };

  const uploadPendingImages = async (reportId: string, details: DetailItem[]) => {
    const uploadPromises: Promise<void>[] = [];

    details.forEach(detail => {
      if (detail.pendingImages.length > 0 && detail.detail) {
        detail.pendingImages.forEach(file => {
          uploadPromises.push(
            carReportsApi.uploadReportImage(reportId, detail.detail, file)
              .then(newComment => {
                setDetails(prev => prev.map(d =>
                  d.detail === detail.detail
                    ? { ...d, comments: [...d.comments, newComment] }
                    : d
                ));
              })
              .catch(error => {
                console.error(`Failed to upload image for detail ${detail.detail}:`, error);
              })
          );
        });
      }
    });

    await Promise.all(uploadPromises);
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      const reportId = await createReport.mutateAsync({
        renting: rentingId,
        activity: activities?.[formData.activity_type as keyof typeof activities] || '',
        activity_type: formData.activity_type || 'delivery_to_client',
        mileage: formData.mileage || 0,
        fuel_level: formData.fuel_level || 0,
        charge_level: formData.charge_level || 0,
        details: details.map(d => ({
          detail: d.detail,
          comments: d.comments.filter(c => c.text.trim() || c.file_url)
        }))
      });

      // Загружаем изображения после создания отчета
      if (reportId?.id && details.some(d => d.pendingImages.length > 0)) {
        setIsUploadingImages(true);
        try {
          await uploadPendingImages(reportId.id, details);
        } finally {
          setIsUploadingImages(false);
        }
      }
      onSubmit();
      toast.success(t('cars.report.create_success'));
    } catch (error) {
      toast.error(t('cars.report.create_error'));
    }
  };

  const handleMileageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      mileage: value === '' ? 0 : parseInt(value)
    }));
    if (errors.mileage) {
      setErrors(prev => ({ ...prev, mileage: undefined }));
    }
  };

  const handleFuelLevelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      fuel_level: value === '' ? 0 : parseInt(value)
    }));
    if (errors.fuel_level) {
      setErrors(prev => ({ ...prev, fuel_level: undefined }));
    }
  };

  const handleChargeLevelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      charge_level: value === '' ? 0 : parseInt(value)
    }));
    if (errors.charge_level) {
      setErrors(prev => ({ ...prev, charge_level: undefined }));
    }
  };

  if (isLoadingProfile || isLoadingReports) {
    return <div>{t('Loading...')}</div>;
  }

  return (
    <>

      <div className="">
        <div className="grid grid-cols-1 gap-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Label>{t('cars.report.fields.report_type')}</Label>
              <select
                data-testid="report-form-field-activity-type"
                className="w-full rounded-lg border border-border-input bg-bg-input px-4 py-2.5 outline-none text-text-primary focus:border-primary focus-visible:shadow-none disabled:opacity-50 disabled:cursor-not-allowed"
                value={formData.activity_type}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    activity_type: e.target.value as 'delivery_to_client' | 'pickup_from_client' | 'handoff_to_service' | 'receiving_from_service',
                  }))
                }
              >
                {availableReportTypes.map(type => (
                  <option
                    key={type.value}
                    value={type.value}
                    disabled={type.disabled}
                  >
                    {type.label} {type.disabled ? t('cars.report.options.already_exists') : ''}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label>{t('cars.report.fields.mileage')}</Label>
              <Input
                testId="report-form-field-mileage"
                type="number"
                value={formData.mileage === 0 ? '' : formData.mileage}
                onChange={handleMileageChange}
                placeholder={t('cars.report.placeholders.enter_mileage')}
                error={errors.mileage}
              />
            </div>

            <div>
              <Label>{t('cars.report.fields.fuel_level')}</Label>
              <Input
                testId="report-form-field-fuel-level"
                type="number"
                min="0"
                max="100"
                value={formData.fuel_level === 0 ? '' : formData.fuel_level}
                onChange={handleFuelLevelChange}
                placeholder={t('cars.report.placeholders.enter_fuel_level')}
                error={errors.fuel_level}
              />
            </div>

            <div>
              <Label>{t('cars.report.fields.charge_level')}</Label>
              <Input
                testId="report-form-field-charge-level"
                type="number"
                min="0"
                max="100"
                value={formData.charge_level === 0 ? '' : formData.charge_level}
                onChange={handleChargeLevelChange}
                placeholder={t('cars.report.placeholders.enter_charge_level')}
                error={errors.charge_level}
              />
            </div>
          </div>

          <div>
            {errors.details && (
              <p className="text-sm text-danger mb-2">{errors.details}</p>
            )}

            {/* Интерактивное изображение автомобиля */}
            {techProfile && (
              <div className="mb-6">
                <InteractiveCarImage
                  details={techProfile.details}
                  selectedDetails={details.map(d => d.detail).filter(Boolean)}
                  onDetailSelect={handleAddDetail}
                  onDetailDeselect={handleRemoveDetail}
                  availableDetails={availableDetails}
                  onDetailFocus={(detailId) => {
                    const detail = details.find(d => d.detail === detailId);
                    if (detail) {
                      const textarea = document.querySelector(`[data-testid="report-form-detail-comment-${detail.id}"]`) as HTMLTextAreaElement;
                      if (textarea) {
                        textarea.focus();
                        textarea.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        // Добавляем визуальное выделение
                        textarea.style.borderColor = '#3b82f6';
                        textarea.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                        setTimeout(() => {
                          textarea.style.borderColor = '';
                          textarea.style.boxShadow = '';
                        }, 2000);
                      }
                    }
                  }}
                />
              </div>
            )}

            {/* Список выбранных деталей с комментариями и изображениями - под изображением */}
            {details.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4 text-text-primary">
                  Selected Details ({details.length})
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-[1200px]">
              {details.map((detail) => {
                const detailInfo = techProfile?.details.find(d => d.detail === detail.detail);
                return (
                  <div
                    key={detail.id}
                    className="border border-border-primary p-4 rounded-lg"
                  >
                    <div className="flex justify-between items-center mb-4">
                      <Label>{detailInfo?.name || detail.detail}</Label>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRemoveDetail(detail.detail)}
                        className="!p-1.5 border-border-primary hover:border-danger hover:bg-danger/10"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 text-gray-500 group-hover:text-danger"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </Button>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <Label>{t('cars.report.fields.comment')}</Label>
                        <TextArea
                          testId={`report-form-detail-comment-${detail.id}`}
                          value={detail.comments.find(c => !c.file_url)?.text || ''}
                          onChange={(value) => handleDetailChange(detail.detail, 'comment', value)}
                          placeholder={t('cars.report.placeholders.add_comment')}
                          rows={2}
                        />
                      </div>
                      <div>
                        <Label>{t('cars.report.fields.images_to_upload')}</Label>
                        <div className="flex flex-wrap gap-2">
                          {detail.pendingImages.map((file, index) => (
                            <div key={index} className="relative group">
                              <div className="w-20 h-20 rounded overflow-hidden bg-gray-100 flex items-center justify-center">
                                <img
                                  src={URL.createObjectURL(file)}
                                  alt={file.name}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <button
                                onClick={() => handleRemovePendingImage(detail.detail, index)}
                                className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow-md hover:bg-gray-100"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-4 w-4 text-gray-500"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </button>
                            </div>
                          ))}
                          <label className="w-20 h-20 flex items-center justify-center border-2 border-dashed border-gray-300 rounded cursor-pointer hover:border-primary">
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  handleAddPendingImage(detail.detail, file);
                                }
                              }}
                            />
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-6 w-6 text-gray-400"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 4v16m8-8H4"
                              />
                            </svg>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="px-6 py-4">
        <div className="flex items-center gap-3 sm:justify-end">
          <Button
            testId="report-form-cancel-button"
            variant="outline"
            size="sm"
            onClick={onClose}
          >
            {t('common.buttons.cancel')}
          </Button>
          <Button
            testId="report-form-submit-button"
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={createReport.isPending || isUploadingImages}
          >
            {createReport.isPending ? t('cars.report.buttons.creating') :
             isUploadingImages ? t('cars.report.buttons.uploading') : t('cars.report.buttons.create')}
          </Button>
        </div>
      </div>
    </>
  );
};

CarReportForm.displayName = 'CarReportForm';

export default CarReportForm;
